import { useEffect } from 'react';
import { useAuthStore } from '@stores/authStore';
import { useVoiceAgentStore, IncomingCallContext } from '@stores/voiceAgentStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { getAccessToken } from '@/lib/tokenStorage';
import voiceService from '@services/voice.service';
import { BridgeCall } from '../bridge/BridgeCall';
import type { CallParticipant } from '../callConference';

/** A handoff offered to this agent, as the BFF sends it on voice:handoff:offered. */
interface HandoffOffer {
  sessionId: string;
  attemptId?: string;
  agentId?: string;
  state?: string;
  strategy?: string;
  from?: string;
  reason?: string;
  offer?: Record<string, string>;
  /** Colleagues added to the call (a 3-way call). */
  participants?: CallParticipant[];
  /** Set on an invitation to join a colleague's call. */
  invite?: { by?: string };
  /** A colleague came off the call, and why. */
  conferenceOutcome?: { agentId: string; outcome: string };
}

/** The incoming-call details, from the context the server attached to the offer. */
export function contextFromOffer(data: HandoffOffer): IncomingCallContext {
  const offer = data.offer ?? {};
  const context: IncomingCallContext = {
    customerPhone: offer.customerPhone || data.from || 'Unknown',
    handoffReason: offer.handoffReason || data.reason,
    ticketId: offer.ticketId || undefined,
    sessionId: data.sessionId,
    transferredBy: offer.transferredBy || undefined,
    transferNote: offer.transferNote || undefined,
    transferReturned: offer.transferReturned === 'true' || undefined,
    invitedBy: data.invite ? data.invite.by || offer.invitedBy || 'A colleague' : undefined,
  };
  if (offer.aiSummary) {
    try {
      context.aiSummary = JSON.parse(offer.aiSummary);
    } catch {
      // Shown without the summary.
    }
  }
  return context;
}

/** What happened to a colleague this agent tried to add, in the agent's terms. */
export function conferenceOutcomeNotice(name: string, outcome: string): string | null {
  switch (outcome) {
    case 'declined':
      return `${name} declined to join the call.`;
    case 'timeout':
    case 'no_answer':
      return `${name} did not answer. You are still on the call.`;
    case 'left':
      return `${name} left the call.`;
    default:
      return null;
  }
}

/** Why accepting a call did not connect, in the agent's terms. */
export function acceptFailure(error: unknown): string {
  const name = (error as { name?: string } | undefined)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'The call could not be connected: your microphone is blocked. Allow it for this site in the address bar.';
  }
  if (name === 'NotFoundError') {
    return 'The call could not be connected: no microphone was found.';
  }
  return 'The call could not be connected, and has been passed on. The caller may have hung up.';
}

/** Why a call that was ringing for this agent stopped, in the agent's terms. */
export function missedCallNotice(data: HandoffOffer, sameAttempt: boolean): string {
  const who = data.from ? ` from ${data.from}` : '';
  if (data.state === 'abandoned') return `The caller${who} hung up before you answered.`;
  if (data.state === 'connected' && sameAttempt) return `The call${who} was answered in another tab.`;
  if (data.state === 'fallback' || data.state === 'failed') {
    return `You missed a call${who}. No one else was free, so the caller will be called back.`;
  }
  return `You missed a call${who}. It has been passed on.`;
}

/**
 * BridgePhone — the agent's phone for calls the platform bridges itself
 * (the caller's audio streams to us, e.g. Exotel), alongside AgentPhone for
 * Twilio. An offer for this agent rings the same incoming-call modal; the
 * call then behaves like any other in the console.
 */
/**
 * The bridged call this browser is ringing for or talking on. Kept outside the
 * component on purpose: BridgePhone lives in the app layout, and the layout is
 * mounted again now and then - a page under the other layout (the not-found
 * shell), the loading screen while the session refreshes. Held in the
 * component, the call was hung up by that unmount: an agent's call dropped
 * milliseconds after they answered it. Now a remount picks the call up again;
 * only signing out ends it.
 */
const callHolder: { current: BridgeCall | null } = { current: null };

/** Tests only: forget (and end) whatever call a previous test left. */
export function resetBridgePhoneForTests(): void {
  callHolder.current?.disconnect();
  callHolder.current = null;
}

export function BridgePhone() {
  const user = useAuthStore((s) => s.user);
  const { on, off } = useWebSocket();
  const current = callHolder;

  // Signing out (or another user signing in) ends whatever call was here.
  useEffect(() => {
    if (user?.id) return;
    callHolder.current?.disconnect();
    callHolder.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const userId = user.id;

    const onOffered = (data: HandoffOffer) => {
      if (!data?.sessionId || !data.attemptId) return;
      if (data.agentId !== userId || data.strategy !== 'media_bridge' || data.state !== 'connecting') return;
      if (current.current?.attemptId === data.attemptId) return; // duplicate delivery
      // Already talking to someone: never drop that call for a new ring. The
      // server is told so it moves the new caller on at once.
      if (useVoiceAgentStore.getState().activeCall) {
        voiceService.declineHandoff(data.sessionId).catch(() => {});
        return;
      }

      // The server says where with the phone setup; the build setting remains
      // an override for local development.
      const mediaUrl = import.meta.env.VITE_VOICE_MEDIA_URL || useVoiceAgentStore.getState().mediaUrl;
      const token = getAccessToken();
      if (!mediaUrl || !token) {
        console.warn('[BridgePhone] no media address for bridged calls; declining');
        voiceService.declineHandoff(data.sessionId).catch(() => {});
        return;
      }

      const call = new BridgeCall(data.sessionId, data.attemptId, { mediaUrl, token });
      // True once the agent's audio link is open: before that, nobody spoke.
      let connected = false;
      call.on('accept', () => {
        connected = true;
        if (useVoiceAgentStore.getState().activeCall === call) useVoiceAgentStore.getState().setCallLive(true);
      });
      call.on('error', (error) => {
        // Microphone refused or the audio link failed: say so, and let the
        // caller move on to the next agent rather than wait on a dead line.
        voiceService.declineHandoff(data.sessionId).catch(() => {});
        if (!connected) useVoiceAgentStore.getState().setNotice(acceptFailure(error));
      });
      call.on('disconnect', () => {
        if (current.current === call) current.current = null;
        const state = useVoiceAgentStore.getState();
        if (state.incomingCall === call) state.setIncomingCall(null);
        if (state.activeCall === call) {
          state.setActiveCall(null);
          state.reset();
          // A call that never connected has nothing to wrap up.
          if (!connected) state.setWrapUp(null);
        }
      });

      current.current?.disconnect();
      current.current = call;
      useVoiceAgentStore.getState().setIncomingCall(call, contextFromOffer(data));
    };

    // The server moved on (timed out, re-routed, caller left), or this call
    // was answered in another tab: stop ringing, and say why.
    const onUpdated = (data: HandoffOffer) => {
      const call = current.current;
      if (!call || data?.sessionId !== call.sessionId) return;
      const state = useVoiceAgentStore.getState();
      const ringingHere = state.incomingCall === call;
      const answeredHere = state.activeCall === call;
      const sameAttempt = data.attemptId === call.attemptId;
      const participants = data.participants ?? [];
      // On a 3-way call, this console may be the colleague who was added.
      const asGuest = participants.find((p) => p.attemptId === call.attemptId);

      if (data.state === 'connected' && sameAttempt && answeredHere) {
        // Holding the call (or now holding it: the agent who added this one
        // left, and the caller stayed with them).
        if (state.isGuest) state.setGuest(false);
        state.setParticipants(participants);
        const outcome = data.conferenceOutcome;
        if (outcome) {
          const who = state.participants.find((p) => p.agentId === outcome.agentId);
          const notice = conferenceOutcomeNotice(who?.agentName || 'Your colleague', outcome.outcome);
          if (notice) state.setNotice(notice);
        }
        return;
      }
      if (data.state === 'connected' && asGuest) {
        // Invited and still ringing here, or on the call from here.
        if ((asGuest.state === 'ringing' && ringingHere) || answeredHere) return;
      }
      // An answered call is never ended from here. Status updates travel
      // through Kafka (or its HTTP fallback) and can arrive late or twice: an
      // old "connected" for the colleague who transferred this call used to
      // hang it up seconds after it was answered. The call's own media
      // connection says when it is over - `end` for this attempt, or the
      // caller leaving - and it is the only thing that does.
      if (answeredHere) return;
      if (sameAttempt && data.state === 'connecting') return;

      call.disconnect();
      if (!ringingHere) return;
      // This agent is transferring it themselves: it did not get away from them.
      if (state.transferringSessionId === data.sessionId) return;
      state.setNotice(
        state.callContext?.invitedBy
          ? `The invitation to join ${state.callContext.invitedBy}'s call ended.`
          : missedCallNotice(data, sameAttempt),
      );
    };

    on('voice:handoff:offered', onOffered);
    on('voice:handoff:updated', onUpdated);
    return () => {
      off('voice:handoff:offered', onOffered);
      off('voice:handoff:updated', onUpdated);
      // Not a hang-up: the layout is being mounted again, and the call
      // (ringing or answered) carries on with the next BridgePhone.
    };
  }, [user?.id, on, off]);

  return null;
}
